import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, Plus, Edit, Trash2, ArrowLeft, Search, MoreHorizontal, DollarSign,
  ShoppingCart, Briefcase, Check, X, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

export default function ProductsServicesPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    type: "product",
    description: "",
    base_price: "",
    currency: "USD",
    unit: "unit",
    sku: "",
    is_active: true
  });

  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/products-services");
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching products/services:", error);
      if (error.response?.status === 403) {
        toast.error("This feature is only available for business accounts");
        navigate("/dashboard");
      } else {
        toast.error(t("failed_load_data", "dashboard") || "Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openCreateDialog();
    }
  }, [searchParams]);

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      type: "product",
      description: "",
      base_price: "",
      currency: "USD",
      unit: "unit",
      sku: "",
      is_active: true
    });
    setShowDialog(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      type: item.type,
      description: item.description || "",
      base_price: item.base_price.toString(),
      currency: item.currency,
      unit: item.unit,
      sku: item.sku || "",
      is_active: item.is_active
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a name");
      return;
    }
    if (!formData.base_price || parseFloat(formData.base_price) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        base_price: parseFloat(formData.base_price)
      };

      if (editingItem) {
        await api.put(`/products-services/${editingItem.id}`, payload);
        toast.success(t("item_updated", "company") || "Item updated successfully");
      } else {
        await api.post("/products-services", payload);
        toast.success(t("item_created", "company") || "Item created successfully");
      }
      
      setShowDialog(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/products-services/${deleteConfirm.id}`);
      toast.success(t("item_deleted", "company") || "Item deleted successfully");
      setDeleteConfirm(null);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.sku && item.sku.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === "all" || item.type === filterType;
    return matchesSearch && matchesType;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="max-w-[1200px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1200px] mx-auto px-4 py-6">
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="products-services-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1200px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <Package className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("products_services", "company") || "Products & Services"}
              </h1>
              <p className="text-xs text-muted-foreground">{items.length} {t("items", "common") || "items"}</p>
            </div>
          </div>
          
          <Button onClick={openCreateDialog} data-testid="add-item-button">
            <Plus className="h-4 w-4 mr-2" />
            {t("add_item", "company") || "Add Item"}
          </Button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 py-6 space-y-4">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("search_items", "company") || "Search products or services..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]" data-testid="type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_types", "company") || "All Types"}</SelectItem>
              <SelectItem value="product">{t("products", "company") || "Products"}</SelectItem>
              <SelectItem value="service">{t("services", "company") || "Services"}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Items Table */}
        <Card>
          <CardContent className="p-0">
            {filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery ? (t("no_items_found", "company") || "No items match your search") : (t("no_items_yet", "company") || "No products or services yet")}
                </p>
                {!searchQuery && (
                  <Button className="mt-4" onClick={openCreateDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("add_first_item", "company") || "Add Your First Item"}
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("name", "common") || "Name"}</TableHead>
                    <TableHead>{t("type", "common") || "Type"}</TableHead>
                    <TableHead>{t("price", "company") || "Price"}</TableHead>
                    <TableHead>{t("unit", "company") || "Unit"}</TableHead>
                    <TableHead>{t("sales", "company") || "Sales"}</TableHead>
                    <TableHead>{t("status", "common") || "Status"}</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRow key={item.id} data-testid={`item-row-${item.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.name}</p>
                          {item.sku && <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {item.type === "product" ? (
                            <><ShoppingCart className="h-3 w-3 mr-1" />{t("product", "company") || "Product"}</>
                          ) : (
                            <><Briefcase className="h-3 w-3 mr-1" />{t("service", "company") || "Service"}</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(item.base_price, item.currency)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{item.unit}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{item.total_sales || 0} {t("sales", "company") || "sales"}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(item.total_revenue, item.currency)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.is_active ? "default" : "secondary"}>
                          {item.is_active ? (t("active", "common") || "Active") : (t("inactive", "common") || "Inactive")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`item-menu-${item.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(item)}>
                              <Edit className="h-4 w-4 mr-2" />
                              {t("edit", "common") || "Edit"}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-destructive" 
                              onClick={() => setDeleteConfirm(item)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t("delete", "common") || "Delete"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? (t("edit_item", "company") || "Edit Item") : (t("add_item", "company") || "Add Item")}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? (t("edit_item_desc", "company") || "Update the details below") : (t("add_item_desc", "company") || "Add a new product or service")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("type", "common") || "Type"}</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                <SelectTrigger data-testid="item-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">{t("product", "company") || "Product"}</SelectItem>
                  <SelectItem value="service">{t("service", "company") || "Service"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("name", "common") || "Name"} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={t("item_name_placeholder", "company") || "Enter item name"}
                data-testid="item-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t("description", "common") || "Description"}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t("item_description_placeholder", "company") || "Enter description (optional)"}
                rows={3}
                data-testid="item-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("price", "company") || "Price"} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.base_price}
                  onChange={(e) => setFormData({...formData, base_price: e.target.value})}
                  placeholder="0.00"
                  data-testid="item-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("unit", "company") || "Unit"}</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}>
                  <SelectTrigger data-testid="item-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unit">{t("unit_unit", "company") || "Unit"}</SelectItem>
                    <SelectItem value="hour">{t("unit_hour", "company") || "Hour"}</SelectItem>
                    <SelectItem value="project">{t("unit_project", "company") || "Project"}</SelectItem>
                    <SelectItem value="subscription">{t("unit_subscription", "company") || "Subscription"}</SelectItem>
                    <SelectItem value="session">{t("unit_session", "company") || "Session"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("sku", "company") || "SKU"} ({t("optional", "common") || "optional"})</Label>
              <Input
                value={formData.sku}
                onChange={(e) => setFormData({...formData, sku: e.target.value})}
                placeholder={t("sku_placeholder", "company") || "Stock keeping unit"}
                data-testid="item-sku-input"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>{t("active", "common") || "Active"}</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                data-testid="item-active-switch"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={isSaving}>
              {t("cancel", "common") || "Cancel"}
            </Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-item-button">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? (t("update", "common") || "Update") : (t("create", "common") || "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete", "common") || "Confirm Delete"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_item_warning", "company") || "Are you sure you want to delete"} "{deleteConfirm?.name}"? {t("action_irreversible", "common") || "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel", "common") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("delete", "common") || "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
